import {createContext, useMemo, type ReactNode} from 'react';
import {Monorail} from '@shopify/monorail';
import {isProduction} from 'packages/@web-utilities/env';
import {useI18n} from '@shopify/react-i18n';
import {useShop} from '@web-utilities/shop';

import type {Schema, GlobalPayload} from './types';
import {Schemas} from './schema-types';
import {Producer} from './types';
import {IgnoreErrorMiddleware} from './middleware';

const defaultProducer: MonorailContextProps = {
  produce(event: any) {
    // eslint-disable-next-line no-console
    console.log('Monorail event produced', event);

    return Promise.resolve({});
  },
};

export interface MonorailContextProps {
  produce: Producer<Schemas>['produce'];
}

interface MonorailProviderProps {
  children: ReactNode;
}

export const MonorailContext =
  createContext<MonorailContextProps>(defaultProducer);

export function MonorailProvider({children}: MonorailProviderProps) {
  const [i18n] = useI18n();
  const {shopId} = useShop();

  const monorail = useMemo(
    () =>
      createProducer({
        locale: i18n.locale,
        shopId: parseInt(shopId, 10),
        userId: 0,
        isShopifyEmployee: false,
      }),
    [i18n.locale, shopId],
  );

  return (
    <MonorailContext.Provider value={monorail}>
      {children}
    </MonorailContext.Provider>
  );
}

export function createProducer(
  globalPayload?: GlobalPayload,
): Producer<Schemas> {
  const producer = isProduction
    ? Monorail.createHttpProducer({
        production: true,
        middleware: [new IgnoreErrorMiddleware()],
      })
    : Monorail.createLogProducer({debugMode: true});

  return {
    produce({schemaId, payload}: Schema) {
      const event = {
        schemaId,
        payload: {
          ...(globalPayload || {}),
          ...(payload as {}),
        },
      };

      return producer.produce(event as Schemas);
    },
  };
}
